import React, { useEffect, useState } from 'react';
import { CheckCircleIcon, XMarkIcon } from '@heroicons/react/24/outline';
import { CATEGORIAS_PENDENCIA, type CategoriaPendencia, type ResumoDivergencia } from '../../types/conferencia';
import { sugerirCategoriasPendencia } from '../../utils/statusConferencia';
import { btnPrimario, btnSecundario } from '../ui/iris';
import DivergenciaResumo from './DivergenciaResumo';

interface ConcluirConferenciaDialogProps {
  isOpen: boolean;
  resumo: ResumoDivergencia;
  processando: boolean;
  onCancelar: () => void;
  onConcluir: (categorias: CategoriaPendencia[], observacao: string) => void;
}

/**
 * Diálogo de conclusão da conferência com declaração de pendência. As categorias vêm
 * pré-marcadas a partir da divergência (sugerirCategoriasPendencia); o conferente ajusta.
 * Concluir com ≥1 categoria = nota com pendência; nenhuma = conclusão normal — sempre permitido.
 */
const ConcluirConferenciaDialog: React.FC<ConcluirConferenciaDialogProps> = ({
  isOpen, resumo, processando, onCancelar, onConcluir,
}) => {
  const [marcadas, setMarcadas] = useState<Set<CategoriaPendencia>>(() => new Set());
  const [observacao, setObservacao] = useState('');
  // Pré-marca só quando abre — a divergência não muda com o diálogo aberto, e reagir a toda
  // mudança de `resumo` sobrescreveria o ajuste manual do conferente enquanto o diálogo está
  // aberto (ex.: ele desmarca "excesso" e um patch otimista de outro item re-renderiza o resumo).
  const [inicializado, setInicializado] = useState(false);

  useEffect(() => {
    if (isOpen && !inicializado) {
      setMarcadas(new Set(sugerirCategoriasPendencia(resumo)));
      setInicializado(true);
    }
    if (!isOpen && inicializado) {
      setInicializado(false);
      setObservacao('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, inicializado]);

  // Fechar com ESC + travar scroll do body enquanto aberto (mesmo molde de FotosConferenciaDialog).
  useEffect(() => {
    if (!isOpen) return;
    const onEsc = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancelar(); };
    window.addEventListener('keydown', onEsc);
    const original = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onEsc);
      document.body.style.overflow = original;
    };
  }, [isOpen, onCancelar]);

  if (!isOpen) return null;

  const toggle = (c: CategoriaPendencia) => {
    setMarcadas((prev) => {
      const next = new Set(prev);
      if (next.has(c)) next.delete(c); else next.add(c);
      return next;
    });
  };

  return (
    <div className="fixed inset-0 z-50" onClick={onCancelar}>
      <div className="fixed inset-0 bg-[var(--scrim)] backdrop-blur-[2px]" />
      <div className="fixed inset-0 flex flex-col items-center justify-center p-4">
        <div
          role="dialog"
          aria-modal="true"
          aria-label="Concluir conferência"
          className="relative flex max-h-full w-full max-w-md flex-col overflow-hidden rounded-iris-lg border border-line-strong bg-surface shadow-iris-xl"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
            <h2 className="text-base font-semibold text-tx">Concluir conferência</h2>
            <button
              type="button"
              onClick={onCancelar}
              aria-label="Fechar"
              className="flex h-11 w-11 items-center justify-center rounded-iris text-tx-3 hover:bg-surface-2 hover:text-tx md:h-8 md:w-8"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            <div className="flex items-center justify-between gap-2">
              <p className="text-sm text-tx-2">
                {resumo.pendentes + resumo.falta + resumo.excedente + resumo.avaria === 0
                  ? 'Todos os itens conferem com a nota.'
                  : [
                      resumo.pendentes > 0 ? `${resumo.pendentes} não conferido(s)` : null,
                      resumo.falta > 0 ? `${resumo.falta} falta` : null,
                      resumo.excedente > 0 ? `${resumo.excedente} excedente(s)` : null,
                      resumo.avaria > 0 ? `${resumo.avaria} avaria(s)` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ')}
              </p>
            </div>
            <div className="mt-2">
              <DivergenciaResumo resumo={resumo} />
            </div>

            <div className="mt-4">
              <p className="text-[10px] font-medium uppercase tracking-wide text-tx-3">Registrar pendência (opcional)</p>
              <p className="mt-0.5 text-[11px] text-tx-3">Marque se esta nota precisa ser tratada depois.</p>
              <div className="mt-2 space-y-1.5">
                {CATEGORIAS_PENDENCIA.map((cat) => (
                  <label
                    key={cat.chave}
                    className="flex min-h-11 items-center gap-2.5 rounded-iris-sm border border-line bg-surface-2 px-3 py-2 text-sm text-tx md:min-h-0"
                  >
                    <input
                      type="checkbox"
                      checked={marcadas.has(cat.chave)}
                      onChange={() => toggle(cat.chave)}
                      className="h-4 w-4 accent-[var(--accent)]"
                    />
                    {cat.label}
                  </label>
                ))}
              </div>
              <textarea
                value={observacao}
                onChange={(e) => setObservacao(e.target.value)}
                placeholder="Observação da pendência (opcional)…"
                rows={2}
                maxLength={500}
                className="mt-2 w-full resize-none rounded-iris-sm border border-line-strong bg-surface-2 px-3 py-2 text-sm text-tx placeholder:text-tx-3 focus:border-accent focus:bg-surface focus:outline-none focus:ring-[3px] focus:ring-accent-soft"
              />
            </div>
          </div>

          <div className="flex flex-col-reverse gap-2 border-t border-line bg-surface-2 px-4 py-3 sm:flex-row sm:justify-end">
            <button type="button" onClick={onCancelar} disabled={processando} className={`${btnSecundario} w-full sm:w-auto`}>
              Cancelar
            </button>
            <button
              type="button"
              disabled={processando}
              onClick={() => onConcluir(Array.from(marcadas), observacao.trim())}
              className={`${btnPrimario} w-full sm:w-auto`}
            >
              <CheckCircleIcon className="h-4 w-4" />
              {processando ? 'Concluindo…' : marcadas.size > 0 ? 'Concluir com pendência' : 'Concluir'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConcluirConferenciaDialog;
